// wireShape — cast-free readers over untrusted wire JSON.
//
// The HTTP boundary hands the shell `unknown` (a parsed JSON body). These
// helpers narrow that `unknown` structurally so the adapters can read fields
// WITHOUT a typecast — the same idiom `@bilda/core`'s `resource/element.ts`
// uses at its own untrusted-JSON boundary (`typeof x === "object"` + `"k" in
// x`, `Object.entries`). Nothing here throws; every reader is total and
// returns a typed "absent" (`undefined` / `[]`) on a shape mismatch, so a
// caller degrades gracefully instead of crashing on a surprise payload.
//
// Pure: no I/O. This is presentation-layer parsing, not domain logic
// (@bilda/client imports zero @bilda/core) — the shapes mirror the domain wire
// shapes by structure, not by importing their types.

export { bool, fields, list, num, str };

// A record view of an object value, as a Map so reads land in `unknown`
// (never `any`). `Object.entries` over the narrowed `object` yields
// `[string, unknown]` pairs into the annotated Map — no cast, no `any` leak.
// Arrays are excluded so `fields([...])` is `undefined`, not an index map.
const fields = (v: unknown): ReadonlyMap<string, unknown> | undefined =>
    typeof v === "object" && v !== null && !Array.isArray(v)
        ? new Map<string, unknown>(Object.entries(v))
        : undefined;

// A string value, or `undefined` when the field is absent / non-string.
const str = (v: unknown): string | undefined =>
    typeof v === "string" ? v : undefined;

// A boolean value; anything that isn't literally `true` reads as `false`
// (a missing `correct` on a choice is "not the key", never a crash).
const bool = (v: unknown): boolean => v === true;

// A finite number, or `undefined` when the field is absent / non-numeric /
// NaN — so a caller can supply its own fallback rather than propagate a NaN.
const num = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) ? v : undefined;

// An array view, or the empty list when the field is absent / non-array —
// so a `for (… of list(x))` never has to guard.
const list = (v: unknown): readonly unknown[] => (Array.isArray(v) ? v : []);
